/**
 * The move to sparrowword.com (docs/LAUNCH.md, "sparrowword.com"). A player's streak,
 * stats, saves, settings and stacks live in the old address's localStorage, which only a
 * page on that address can read. So when the app opens on an old address it doesn't
 * start; it hands the data over:
 *
 * 1. It retires the service worker there (and its caches), so nobody is kept on an old app.
 * 2. Nothing played there: straight to the same path on the site.
 * 3. Otherwise it parks a snapshot for a one-time code (`POST /v1/carry`, the same
 *    mechanism as the iPhone install, pwa/carry.ts) and goes to the same path on the site
 *    with `#carry=CODE` (a fragment: never sent to a server). The carry failed (offline,
 *    the api down): it goes anyway, with `#carry-failed`, and the site offers to try
 *    again. The old address keeps its data, so a later visit there hands it over again.
 * 4. Opened from the home screen (an app installed from the old address): no redirect,
 *    which would leave the player in a browser view inside the old app. It shows
 *    "Sparrow has moved" with a link that carries the code (`?carry=CODE&moved=1`), and
 *    asks them to add it to the home screen again from there.
 *
 * On the site, `consumeCarryOnLaunch` (install.ts) imports the code once, merging into
 * whatever is there already (`combineSnapshot`: nothing is lost on either side), strips it
 * from the address, and leaves a note for Today ("came with you", or "try again").
 */
import { localDateKey } from '@arrow/game-engine';

import { createCarry } from '../api/client.ts';
import { LEGACY_ORIGINS_RAW, SITE_URL } from '../env.ts';
import { getDeviceId } from '../platform/identity.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { buildSnapshot, CARRY_CODE, CARRY_PARAM } from './carry.ts';
import { parseOrigins } from './legacy-origins.ts';

export { parseOrigins };

/** The fragment the old address hands over with: `#carry=CODE`, or `#carry-failed`. */
export const MOVE_FRAGMENT = 'carry';
export const MOVE_FAILED_FRAGMENT = 'carry-failed';
/** On a link out of the old home-screen app (`?carry=CODE&moved=1`): merge like a move. */
export const MOVED_PARAM = 'moved';
/** Today's note about the move: `ok` (it came across) or `failed` (offer to try again). */
export const MOVE_NOTE_KEY = 'arrow:v1:moved';
/** The last few codes imported here, so a code is only ever imported once. */
export const USED_CODES_KEY = 'arrow:v1:carry:used';

/** The old addresses (`VITE_LEGACY_ORIGINS`, comma-separated origins). */
export const LEGACY_ORIGINS: readonly string[] = parseOrigins(LEGACY_ORIGINS_RAW);

/** This page is on an old address (and not, somehow, on the site itself). */
export function isLegacyOrigin(origin: string, site: string = SITE_URL, legacy: readonly string[] = LEGACY_ORIGINS): boolean {
	try {
		return origin !== new URL(site).origin && legacy.includes(origin);
	} catch {
		return false;
	}
}

export type HandOver = { code: string } | { failed: true } | null;

/**
 * The same page on the site: path and query kept (a stale `carry` param dropped), and
 * the hand-over in the fragment (the home screen's `source` dropped). With `link` (out of the old home-screen app, where a
 * fragment can't ride through to the browser): `?carry=CODE&moved=1` instead.
 */
export function movedUrl(site: string, here: string, handOver: HandOver, link = false): string {
	const from = new URL(here);
	const to = new URL(`${from.pathname}${from.search}`, site);
	to.searchParams.delete(CARRY_PARAM);
	to.searchParams.delete(MOVED_PARAM);
	// It opens in a browser, not from the home screen.
	if (to.searchParams.get('source') === 'homescreen') to.searchParams.delete('source');
	if (link && handOver && 'code' in handOver) {
		to.searchParams.set(CARRY_PARAM, handOver.code);
		to.searchParams.set(MOVED_PARAM, '1');
	}
	to.hash = link || !handOver ? '' : 'code' in handOver ? `${MOVE_FRAGMENT}=${handOver.code}` : MOVE_FAILED_FRAGMENT;
	return to.toString();
}

/** What the old address handed over in `hash`, or null. */
export function readMoveFragment(hash: string): HandOver {
	const h = hash.replace(/^#/, '');
	if (h === MOVE_FAILED_FRAGMENT) return { failed: true };
	const m = new RegExp(`^${MOVE_FRAGMENT}=([^&]+)$`).exec(h);
	return m && CARRY_CODE.test(m[1]!) ? { code: m[1]! } : null;
}

/** Whether there's anything here worth carrying (a first visit has nothing). */
export function hasDataToCarry(store: KeyValueStore, now: Date = new Date()): boolean {
	return Object.keys(buildSnapshot(store, localDateKey(now), now).keys).length > 0;
}

/** Was `code` imported here already? Never throws. */
export function codeUsed(store: KeyValueStore, code: string): boolean {
	try {
		const list = JSON.parse(store.get(USED_CODES_KEY) ?? '[]') as unknown;
		return Array.isArray(list) && list.includes(code);
	} catch {
		return false;
	}
}

export function noteCodeUsed(store: KeyValueStore, code: string): void {
	try {
		const list = JSON.parse(store.get(USED_CODES_KEY) ?? '[]') as unknown;
		const prev = Array.isArray(list) ? list.filter((c): c is string => typeof c === 'string' && c !== code) : [];
		store.set(USED_CODES_KEY, JSON.stringify([...prev, code].slice(-10)));
	} catch {
		// At worst a reload imports it again, which merges to the same thing.
	}
}

export type MoveNote = 'ok' | 'failed';

export function moveNote(store: KeyValueStore): MoveNote | null {
	const v = store.get(MOVE_NOTE_KEY);
	return v === 'ok' || v === 'failed' ? v : null;
}

/**
 * On the old address: a carry code for this device's data, within `timeoutMs`; null when
 * it couldn't be made (the caller still moves on). Never throws.
 */
export async function carryForMove(store: KeyValueStore, timeoutMs = 6000, now: Date = new Date()): Promise<string | null> {
	const ctrl = new AbortController();
	const timer = setTimeout(() => ctrl.abort(), timeoutMs);
	try {
		const snapshot = buildSnapshot(store, localDateKey(now), now);
		const res = await createCarry(getDeviceId(store), snapshot, ctrl.signal);
		return res?.code ?? null;
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
}

/**
 * On the old address: the service worker unregistered and its caches gone, so the next
 * visit reaches the network (and this hand-over) rather than a cached old app. At most
 * `timeoutMs`; never throws.
 */
export async function retireServiceWorker(timeoutMs = 1500): Promise<void> {
	const work = (async () => {
		try {
			const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
			await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
		} catch {
			// Nothing registered, or no service workers here.
		}
		try {
			if (typeof caches !== 'undefined') {
				const names = await caches.keys();
				await Promise.all(names.filter((n) => n.startsWith('sparrow-')).map((n) => caches.delete(n)));
			}
		} catch {
			// The caches go when the worker does, in any case.
		}
	})();
	await Promise.race([work, new Promise((r) => setTimeout(r, timeoutMs))]);
}
